import React, { useMemo, useRef } from 'react'
import moment from 'moment'
import { authorization } from '../../../../../Module/Auth/Auth'
import { StatusLabel } from '../../../../../Components/StatusLabel'
import { LinkView } from '../../../../../Components/Link/View'
import {
  dropTimeZoneFromDate,
  getShortWeekDay,
  browserSafeNewDate,
} from '../../../../../shared/libraries/utils/date'
import { DATETIME_PICKER_FORMAT } from '../../../../../constants'
import { calculateTimeOfDay } from '../../../../../Utils/Mission'
import RosterMissionsTableView from '../component/RosterMissionsTableView'
import {
  dateSort,
  timeOfDaySort,
  dayOfWeekSort,
  statusSort,
} from '../../../../../Utils/Table'
import {
  STATUS_BOOKED,
  MISSION_STATUS_ENUM_MAP,
  MISSION_STATUS_ENUM,
  LOCATION_STATUS_ENUM,
  CAMPAIGN_ENUM_TYPES,
  MISSION_ENUM_TYPES,
} from '../../../../../shared/constants'
import {
  numberFormatter,
  numberFormatterWithOptional2Dp,
} from './../../../../../Utils/Formatters'
import { getLocalTimeSlotFromDateTime } from './../../../../../shared/mission'
import { getMissionDisplayStatus } from './../../../../../shared/missionReadyToPublish'
import {
  DateTimePicker,
  Input,
  SelectDashboard,
} from '../../../../../Components/Form'
import Button from '../../../../../Components/Buttons/Button'
import SmartBASearch from '../../../../../Components/SmartBASearch'
import { Checkbox } from '@material-ui/core'
import { getEditableFields as editableFieldsMissionDetails } from '../../../../Form/Mission/EditMissionDetails/util'
// import ConfirmDialog from '../../../../../Components/ConfirmDialog/ConfirmDialog'
// import { handleConfirmationDialog } from '../../../../City/Page/Ambasadors/EmailPipelineView/utils'
import {
  isNeedToHideEmployeeLastName,
  formatLastName,
} from '../../../../../Utils/User'

const getDataForRosterMissionsTable = ({ missions, city }) => {
  const isNeedToHideLastName = isNeedToHideEmployeeLastName()

  const getMissionItemData = ({ mission, city }) => {
    const {
      id,
      location,
      cancelled,
      dateTime,
      flyerCode,
      totalFlyersDistributed,
      status = '',
      draftPayAdjustment = 0,
      automatic,
      user,
      amountScans,
      cvrPredictionPercent,
      bonusPerConversion,
      bonusThreshold,
      teamLeaderGroupId,
      teamLeaderId,
      isReadyToPublish,
    } = mission

    if (location) {
      var { name: locationName, detailedRank } = location
      var timeOfDay = getLocalTimeSlotFromDateTime(
        dropTimeZoneFromDate(dateTime),
      )
      try {
        detailedRank = JSON.parse(detailedRank)
      } catch (e) {
        detailedRank = {}
      }
      if (detailedRank == null) detailedRank = {}

      var rank =
        detailedRank[timeOfDay] != null ? detailedRank[timeOfDay] : null
      var typeAndRank = automatic ? 'A' : 'M'

      if (rank != null) {
        typeAndRank += ' ' + numberFormatterWithOptional2Dp(rank)
      }
    }
    const { firstName = '-', lastName = '' } = user || {}
    const bonusPerConversionValue = bonusPerConversion
      ? bonusPerConversion / 100
      : '-'
    const bonusThresholdValue = bonusThreshold || '-'
    const flyerCodeValue = flyerCode || '-'
    const totalFlyersDistributedValue = totalFlyersDistributed || '-'
    const localDate = browserSafeNewDate(dropTimeZoneFromDate(dateTime))
    const draftPayAdjustmentValue =
      draftPayAdjustment === 0
        ? '-'
        : numberFormatterWithOptional2Dp(draftPayAdjustment / 100)

    let hoverGroupInfo = ''
    if (teamLeaderGroupId) {
      hoverGroupInfo += teamLeaderGroupId
      if (!teamLeaderId) {
        hoverGroupInfo += '-TLA must be booked'
      } else if (teamLeaderId && user && user.id == teamLeaderId) {
        hoverGroupInfo += '-TLA Mission'
      } else {
        hoverGroupInfo += '-Spied missions'
      }
    }

    return {
      id,
      'Name/Location': locationName || ' - ',
      'Day': getShortWeekDay(localDate.getDay()),
      'Date': moment(dropTimeZoneFromDate(dateTime)).format(
        DATETIME_PICKER_FORMAT,
      ),
      'Time of Day': calculateTimeOfDay(
        dateTime,
        city.lunchCheckInTime,
        city.afternoonCheckInTime,
      ),
      'Pay adjustment': draftPayAdjustmentValue,
      'Bonus per conversion': bonusPerConversionValue,
      'Bonus threshold': bonusThresholdValue,
      'Code': flyerCodeValue,
      'Flyers Distributed': totalFlyersDistributedValue,
      'Scans': String(amountScans),
      'Cancelled': cancelled,
      // The DISPLAY status: the Status filter and sorter read this value off
      // the row, so a signed-off draft has to carry 'READY_TO_PUBLISH' here or
      // it can never be filtered for.
      'Status': getMissionDisplayStatus({ status, isReadyToPublish }),
      'BA': `${firstName} ${formatLastName(lastName)}`,
      'Type & Rank': typeAndRank || ' - ',
      'CVR': cvrPredictionPercent
        ? (cvrPredictionPercent * 100).toFixed(2)
        : ' - ',
      automatic,
      isReadyToPublish,
      detailedRank: detailedRank ? detailedRank[timeOfDay] : null,
      hoverGroupInfo,
    }
  }

  return missions
    .slice(0)
    .sort(missionSort)
    .map(mission => getMissionItemData({ mission, city }))
}

const missionSort = function(a, b) {
  if (a.dateTime < b.dateTime) return -1
  if (a.dateTime > b.dateTime) return 1
  return 0
}

const getFooterData = missions => {
  const data = {
    'Name/Location': <strong>Total:</strong>,
    'Day': numberFormatter(missions.length),
    'Flyers Distributed': (
      <strong>
        {numberFormatter(
          missions.reduce((acc, mission) => {
            var distributed = Number(mission['Flyers Distributed'])
            return acc + (isNaN(distributed) ? 0 : distributed)
          }, 0),
        )}
      </strong>
    ),
  }
  const footerData = [data]
  return footerData
}
const getEditableFields = ({
  onEdit,
  missionId,
  missions,
  cityId,
  locations = [],
  isChecked,
  onCheckbox,
  editedMissionData = {},
  bonusOnConversionRefs,
  bonusThresholdRefs,
  isAdmin,
  isBlockToBookBA,
}) => {
  const mission = missions.find(({ id }) => id == missionId)
  const editableFieldsByStatus = editableFieldsMissionDetails(
    mission.status,
    MISSION_ENUM_TYPES.HANDTOHAND,
  )
  const labelToFieldMap = {
    'Name/Location': 'locationId',
    'Date': 'dateTime',
    'Pay adjustment': 'draftPayAdjustment',
    'BA': 'userId',
    'Bonus per conversion': 'bonusOnConversion',
    'Bonus threshold': 'bonusThreshold',
  }
  const result = {
    'Select': (
      <Checkbox
        key={`checkbox-${mission.id}`}
        defaultChecked={true}
        checked={isChecked}
        onChange={onCheckbox}
      />
    ),
    'Name/Location': (
      <div
        style={{
          maxWidth: '300px',
        }}
      >
        <SelectDashboard
          key={`locationId-${mission.id}`}
          options={locations
            .filter(
              ({ status }) =>
                ![
                  LOCATION_STATUS_ENUM.DISABLED,
                  LOCATION_STATUS_ENUM.ARCHIVED,
                ].includes(status),
            )
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(({ name, id }) => ({
              label: name,
              value: id,
            }))}
          input={{
            value:
              editedMissionData.locationId ||
              (mission.location && mission.location.id),
            onChange: locationId => {
              onEdit(
                mission.id,
                {
                  locationId,
                },
                true,
              )
            },
          }}
          initValue={mission.locationId}
          name="locationId"
          cypress_id="roster-edit-locations-dropdown"
        />
      </div>
    ),
    'Date': (
      <DateTimePicker
        key={`dateTime-${mission.id}-${editedMissionData.dateTime}`}
        name="dateTime"
        input={{
          value: editedMissionData.dateTime || mission.dateTime,
          onChange: dateTime =>
            onEdit(
              mission.id,
              {
                dateTime,
              },
              true,
            ),
        }}
        cypress_id="roster-edit-date-time-input"
      />
    ),
    'Pay adjustment': (
      <Input
        key={`draftPayAdjustment-${mission.id}`}
        name="draftPayAdjustment"
        defaultValue={mission.draftPayAdjustment / 100}
        cypress_id="roster-edit-pay-adjustment-input"
        input={{
          value: editedMissionData.draftPayAdjustment,
          onChange: e =>
            onEdit(
              mission.id,
              {
                draftPayAdjustment: e.target.value,
              },
              true,
            ),
        }}
      />
    ),
    'Bonus per conversion':
      mission.status === MISSION_STATUS_ENUM.AVAILABLE ? (
        <Input
          key={`bonusPerConversion-${mission.id}`}
          name="bonusPerConversion"
          defaultValue={
            mission.bonusPerConversion ? mission.bonusPerConversion / 100 : ''
          }
          inputRef={el => (bonusOnConversionRefs.current[missionId] = el)}
          input={{
            value: editedMissionData.bonusPerConversion,
            onChange: async e => {
              await onEdit(
                mission.id,
                { bonusPerConversion: e.target.value },
                true,
              )

              if (bonusOnConversionRefs.current[missionId]) {
                await bonusOnConversionRefs.current[missionId].focus()
              }
            },
          }}
        />
      ) : (
        <span>{mission.bonusPerConversion || '-'}</span>
      ),
    'Bonus threshold':
      mission.status === MISSION_STATUS_ENUM.AVAILABLE && isAdmin ? (
        <Input
          key={`bonusThreshold-${mission.id}`}
          name="bonusThreshold"
          defaultValue={mission.bonusThreshold ? mission.bonusThreshold : ''}
          inputRef={el => (bonusThresholdRefs.current[missionId] = el)}
          input={{
            value: editedMissionData.bonusThreshold,
            onChange: async e => {
              await onEdit(mission.id, { bonusThreshold: e.target.value }, true)

              if (bonusThresholdRefs.current[missionId]) {
                await bonusThresholdRefs.current[missionId].focus()
              }
            },
          }}
        />
      ) : (
        <span>{mission.bonusThreshold || '-'}</span>
      ),
    'BA': (
      <SmartBASearch
        key={`userId-${mission.id}-${editedMissionData.user &&
          editedMissionData.user.id}`}
        selectedUser={editedMissionData.user || mission.user}
        cityId={cityId}
        onSelect={user =>
          onEdit(mission.id, {
            user,
          })
        }
        disabled={isBlockToBookBA}
      />
    ),
  }

  Object.keys(result).forEach(field => {
    if (
      !editableFieldsByStatus.includes(labelToFieldMap[field]) &&
      field !== 'Select'
    ) {
      delete result[field]
    }
  })

  // Letterbox missions are drawn from a boundary and have no location at all,
  // so there is nothing to pick here. Drop the field and let the row keep its
  // read-only value -- rendering the dropdown used to read `location.id` off
  // null and take the whole roster down the moment Edit Missions was clicked.
  if (!mission.location) {
    delete result['Name/Location']
  }

  return result
}

const getHeaders = (
  isEditMode,
  hasMissionsWithCvrPrediction,
  campaignType,
  campaignBonusEnabled,
  isClient,
  isCityManagerAssistant,
) => {
  const shouldShowBonusFields =
    campaignType !== CAMPAIGN_ENUM_TYPES.DIRECTMAIL &&
    campaignBonusEnabled &&
    !isClient

  const headers = [
    isEditMode && 'Select',
    'Name/Location',
    !isEditMode && 'Day',
    'Date',
    !isEditMode && 'Time of Day',
    !authorization.isClient() &&
      !authorization.isClientAdmin() &&
      !isCityManagerAssistant &&
      'Pay adjustment',
    shouldShowBonusFields && !isCityManagerAssistant && 'Bonus per conversion',
    shouldShowBonusFields && !isCityManagerAssistant && 'Bonus threshold',
    'Code',
    !isEditMode && 'Flyers Distributed',
    !isEditMode && 'Scans',
    'Status',
    !isEditMode && 'Type & Rank',
    'BA',
    hasMissionsWithCvrPrediction && 'CVR',
    !isEditMode && 'Actions',
  ]

  return headers
}

const sortOn = [
  'Name/Location',
  'BA',
  'Code',
  'Bonus per conversion',
  'Bonus threshold',
  'Flyers Distributed',
  { label: 'Date', sort: dateSort },
  { label: 'Time of Day', sort: timeOfDaySort },
  { label: 'Day', sort: dayOfWeekSort },
  { label: 'Status', sort: statusSort },
]

const filterOn = [
  'Name/Location',
  { label: 'Day', sort: dayOfWeekSort, sorted: false },
  { label: 'Time of Day', sort: timeOfDaySort, sorted: false },
  { label: 'Status', sort: statusSort },
]

const RosterMissionsTable = ({
  match,
  city,
  missions,
  editedMissionsData,
  cityLocations,
  onDelete,
  onEdit,
  onSave,
  selectedMissionsMap,
  onCheckbox,
  isEditMode,
  setEditMode,
  campaign,
  generateMissionsButton = null,
}) => {
  const bonusOnConversionRefs = useRef([])
  const bonusThresholdRefs = useRef([])

  const isAdmin = authorization.isAdmin()
  const isClient = authorization.isClient()
  const isCityManagerAssistant = authorization.isCityManagerAssistant()

  const data = useMemo(
    () => getDataForRosterMissionsTable({ missions, city }),
    [missions, city],
  )

  const searchOn = ['Name/Location', 'Code', 'BA']

  const postRowRender = mission => {
    const { detailedRank } = mission
    const statusNumber = MISSION_STATUS_ENUM_MAP[mission.Status]

    let automaticNote = mission.automatic
      ? `This mission is automatic the location and time of day may change.`
      : `This mission is manual, it won't change.`
    if (detailedRank != null) {
      automaticNote += ` The location at this time of day is ranked ${numberFormatterWithOptional2Dp(
        detailedRank,
      )} out of 1.`
    } else {
      automaticNote += ` The location is unranked.`
    }

    const missionsWithNormalizedTz = missions.map(mission => ({
      ...mission,
      dateTime: dropTimeZoneFromDate(mission.dateTime),
    }))

    const editableFields =
      isEditMode && statusNumber <= STATUS_BOOKED
        ? getEditableFields({
            onEdit,
            missions: missionsWithNormalizedTz,
            missionId: mission.id,
            locations: cityLocations,
            cityId: city.id,
            onCheckbox: () => onCheckbox(mission.id),
            isChecked: Boolean(selectedMissionsMap[mission.id]),
            editedMissionData: editedMissionsData[mission.id],
            bonusOnConversionRefs,
            bonusThresholdRefs,
            isAdmin,
            isBlockToBookBA: city.teamLeaderEnabled || mission.hoverGroupInfo,
          })
        : {}

    return {
      ...mission,
      'Status': (
        <StatusLabel
          status={
            mission.Cancelled && mission.Status != MISSION_STATUS_ENUM.CANCELLED
              ? `${MISSION_STATUS_ENUM.CANCELLED}`
              : getMissionDisplayStatus({
                  status: mission.Status,
                  isReadyToPublish: mission.isReadyToPublish,
                })
          }
        />
      ),
      'Actions': (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <LinkView
            onClick={() =>
              window
                .open(`${match.url}/missions/${mission.id}`, '_blank')
                .focus()
            }
          />
        </div>
      ),
      'Type & Rank': (
        <span title={automaticNote} style={{ whiteSpace: 'nowrap' }}>
          {mission['Type & Rank']}
        </span>
      ),
      ...editableFields,
    }
  }

  const handleActivateEditMode = () => {
    // if (campaign.isSelfServe) {
    //   handleConfirmationDialog({
    //     title: 'Restricted!',
    //     contentText: `You cannot manually bulk edit mission for self-serve campaigns. If you need to do this, please contact tech.`,
    //     setConfirmDialogData,
    //     rightButtonLable: 'OK',
    //   })
    // } else {
    setEditMode(true)
    // }
  }

  const hasMissionsWithCvrPrediction = missions.some(
    m => m.cvrPredictionPercent !== null,
  )

  return (
    <div>
      {/* {confirmDialogData.isOpen && <ConfirmDialog {...confirmDialogData} />} */}
      {(authorization.isAdmin() || authorization.isCityManager()) && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '280px',
              padding: '16px 0',
            }}
          >
            {generateMissionsButton}
            {!isEditMode && (
              <Button onClick={handleActivateEditMode}>Edit Missions</Button>
            )}
            {isEditMode && [
              <Button onClick={() => setEditMode(false)}>Cancel</Button>,
              <Button
                disabled={
                  !Object.keys(selectedMissionsMap).filter(
                    id => selectedMissionsMap[id],
                  ).length
                }
                onClick={onDelete}
              >
                Delete
              </Button>,
              <Button
                disabled={!Object.keys(editedMissionsData).length}
                onClick={onSave}
              >
                Save
              </Button>,
            ]}
          </div>
        </div>
      )}
      <RosterMissionsTableView
        header={getHeaders(
          isEditMode,
          hasMissionsWithCvrPrediction,
          campaign.type,
          campaign.bonusEnabled,
          isClient,
          isCityManagerAssistant,
        )}
        data={data}
        sortOn={sortOn}
        filterOn={filterOn}
        footerRender={getFooterData}
        postRowRender={postRowRender}
        searchOn={searchOn}
        cypress_id={'roster-page-missions-table'}
        isNeedToShowGroupedMissions={city.teamLeaderEnabled}
      />
    </div>
  )
}

export default React.memo(RosterMissionsTable)
